import { FormikErrors } from 'formik';
import { TFunction } from 'i18next';
import { Dispatch, SetStateAction } from 'react';
import { boolean, object, string } from 'yup';
import { aggregatorType, IOnboardingData } from '../IOnboardingData.model';
import { IProviderCreation } from '../models/providerCreation.models';
import { IStudioName } from '../models/studioName.models';
import { MIN_PASSWORD_LENGTH } from '~/Constants/Values.constants';
import { ICityCountry } from '~/Models/DTO/CoreProvider.model';

export const validateStudioData = (
  t: TFunction,
  data: IOnboardingData,
  setProviderData: Dispatch<SetStateAction<IProviderCreation>>,
  studioNameCheck: IStudioName,
  studioNameContainsAHyperLink: boolean,
  postLogin?: boolean,
  countryList?: ICityCountry[],
) => {
  const errors = {} as FormikErrors<IOnboardingData>;

  if (!data.studioName) {
    errors.studioName = t('onboarding.name.missing');
  } else if (studioNameCheck?.exist) {
    errors.studioName = t('onboarding.studio.studioExistsErrorMessage');
  } else if (studioNameContainsAHyperLink) {
    errors.studioName = t('onboarding.studio.studioNameHyperLinkErrorMessage');
  }

  if (postLogin) {
    if (!data.locationName) {
      errors.locationName = t('onboarding.location.missing');
    }
    if (!data.city) {
      errors.city = t('import.Missing');
    }
    if (!data.street) {
      errors.street = t('import.Missing');
    }
    if (!data.zipCode) {
      errors.zipCode = t('import.Missing');
    }
  }

  if (!data.studioType) {
    errors.studioType = 'missing';
  }

  if (!data.countryName) {
    errors.countryName = 'missing';
  }

  if (!data.aggregatorType && data.aggregatorType !== 0) {
    errors.aggregatorType = 'missing';
  }

  if (!!data?.city && !!data?.countryName) {
    const countryIsValid = countryList?.find((country) => country?.name === data?.countryName);
    // if (!countryIsValid) {
    //   errors.countryName = t('onboarding.error.country.invalid');
    // }
    const cityIsValid = countryIsValid?.cities?.includes(data?.city || '');
    if (!cityIsValid) {
      errors.city = t('onboarding.error.city.invalid');
    }
  }

  setProviderData({
    name: data?.studioName || '',
    email: postLogin ? data.email : '',
    providerCategoryId: data.studioType || 42,
    slug: data.studioSlug,
    timeZoneId: Intl?.DateTimeFormat()?.resolvedOptions().timeZone || 'Europe/Berlin',
    isUSCMember: data.aggregatorType === aggregatorType.usc,
    isOneFitMember: data.aggregatorType === aggregatorType.onefit,
    creationSource: data.creationSource,
    mainLocation: {
      name: data?.locationName,
      country: data.countryName || '',
      city: data?.city || '',
      latitude: data?.latitude || 0,
      longitude: data?.longitude || 0,
      street: data?.street || '',
      zip_code: data?.zipCode || '',
    },
  });

  return errors;
};

export const onboardingValidationScheme = (t: TFunction, isAlreadyAUser: boolean) =>
  object({
    firstName: string()
      .required(t('userService.firstNameRequired'))
      .matches(/(?=.*[A-Za-z\säüö-])/g, t('onboarding.error.firstName.onlyLetters')),
    lastName: string()
      .required(t('userService.lastNameRequired'))
      .matches(/(?=.*[A-Za-z\säöü-])/g, t('onboarding.error.lastName.onlyLetters')),
    email: string()
      .min(1)
      .matches(/^[^@]+@[^@]+\.[^@]+$/g, t('onboarding.personalDetails.email.invalidErrorMessage')),
    password: isAlreadyAUser
      ? string().required(t('userService.passwordIsRequired'))
      : string()
          .required(t('userService.passwordIsRequired'))
          .matches(/[0-9]/, t('onboarding.error.password.oneLetter'))
          .matches(/[!@#$%^&*]/, t('onboarding.error.password.specialLetter'))
          .matches(/[A-Z]/, t('onboarding.error.password.UpperCaseLetter'))
          .matches(
            new RegExp(`(?=.{${MIN_PASSWORD_LENGTH},})`),
            t('onboarding.error.password.length', { length: MIN_PASSWORD_LENGTH }),
          ),
    termsAndConditions: boolean().required(t('onboarding.error.termsAndConditions')).oneOf([true]),
    dpa: boolean().required(t('onboarding.error.termsAndConditions')).oneOf([true]),
    privacyPolicy: boolean().required(t('onboarding.error.termsAndConditions')).oneOf([true]),
  });
